/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { AiCoach_Action as AiCoachAction } from '__generated__/graphql-types';

import { Grid, Typography2 } from '@coursera/cds-core';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import Action from 'bundles/ai-course-coach/components/Conversation/components/Messages/components/RecommendedActions/Action';
import { SYSTEM_RECOMMENDED_ACTIONS } from 'bundles/ai-course-coach/components/constants';
import type { AiCoachRecommendedAction } from 'bundles/ai-course-coach/store/types';
import type { SendMessageFunc } from 'bundles/ai-course-coach/utils/types';
import sessionStorageEx from 'bundles/common/utils/sessionStorageEx';

import _t from 'i18n!nls/learning-assistant-chat-ai';

export const COACH_RECOMMENDED_ACTION_CLICK = 'coursera_coach_recommended_action_click';

const styles = {
  container: css`
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-200);
  `,
  flexContainer: css`
    gap: var(--cds-spacing-100);
  `,
  label: css`
    margin-bottom: var(--cds-spacing-150);
  `,
};

type Props = {
  recommendedActions: AiCoachRecommendedAction;
  sendMessage: SendMessageFunc;
  timestamp: Date;
};

const RecommendedActions = ({ recommendedActions, sendMessage, timestamp }: Props) => {
  const renderableRecommendedActions = React.useMemo(
    () => (recommendedActions || []).filter(({ action }) => !SYSTEM_RECOMMENDED_ACTIONS.includes(action)),
    [recommendedActions]
  );
  const storedTimestampOfClickedMessage = sessionStorageEx.getItem<string>(
    COACH_RECOMMENDED_ACTION_CLICK,
    (value) => value,
    ''
  );

  const isRecommendedAction = React.useMemo(
    () => renderableRecommendedActions.every((obj) => obj.action === AiCoachAction.GenericAction),
    [renderableRecommendedActions]
  );

  // convert learner click timestamp string to date object
  const dateOfClickedMessage = new Date(storedTimestampOfClickedMessage);

  const isLearnerClickTSBeforeMsgTS = timestamp.getTime() <= dateOfClickedMessage.getTime();

  const actionNotClicked = !storedTimestampOfClickedMessage;
  const showPlaceholderText = actionNotClicked || isLearnerClickTSBeforeMsgTS;

  if (!renderableRecommendedActions?.length) {
    return null;
  }

  return (
    <Grid container css={styles.container}>
      {isRecommendedAction && showPlaceholderText && (
        <Typography2 component="p" css={styles.label}>
          {_t('And if you want to continue exploring this topic, try one of these follow-up questions:')}
        </Typography2>
      )}
      <Grid xs={12} container item css={styles.flexContainer} wrap="wrap">
        {renderableRecommendedActions.map(({ action, actionMessage }) => (
          <Action
            action={action}
            actionMessage={actionMessage}
            key={actionMessage}
            sendMessage={sendMessage}
            actionNotClicked={actionNotClicked}
            isRecommendedAction={isRecommendedAction}
          />
        ))}
      </Grid>
    </Grid>
  );
};

export default RecommendedActions;
